<template>
    <div class="font-body overflow-x-hidden">
        <!-- ===================== HEADER ===================== -->
        <LandingNav
            background-color="#FFFFFF"
            logo-light="/hkcm/images/logo-on-light-background.svg"
            logo-small-light="/hkcm/images/logo-small-on-light-background.svg"
            :cta-text="'Analyse-Pakete entdecken'"
            cta-link="/produkte"
            button-gradient-from="#006CFF"
            button-gradient-to="#006CFF"
            :show-ticker="true"
            ticker-text="+++ Jetzt 20x Gold gewinnen! +++ Wir feiern Goldene Ostern +++ 20 % Rabatt auf unsere Analysepakete Gold, Silber, Edelmetalle & Minen +++"
        />

        <!-- ===================== HERO ===================== -->
        <HeroSection
            background-color="radial-gradient(circle at 50% 50%, #072551 0%, #051A39 50%, #020E20 100%)"
            title="Goldene Ostern –<br>für Ihre Zukunft und die Ihrer Kinder"
            title-highlight="Goldene Ostern –"
            title-highlight-gradient-from="#93802F"
            title-highlight-gradient-to="#E2C750"
            subtitle="Gewinnen Sie echtes Gold und sichern Sie sich 20 % Rabatt auf unsere Analysen zu Gold, Silber, Edelmetalle und<br class='lg:block hidden'> Minen."
            cta-text="Jetzt Gold gewinnen"
            cta-link="#so-funktioniert-es"
            :show-cta="true"
            :badges="heroBadges"
            button-gradient-from="#93802F"
            button-gradient-to="#E2C750"
            :social-proof-text="'Bereits über 25.000<br/>zufriedene Kunden'"
            social-proof-image="/hkcm/images/rezensionenBilder.png"
            :app-store-data="appStoreData"
            :google-play-data="googlePlayData"
            checkmark-color="text-white"
            background-image="/hkcm/images/bg_candels.png"
        />

        <!-- ===================== VIDEO ===================== -->
        <VideoSection
            background-color="transparent"
            video-url="https://iframe.mediadelivery.net/embed/369702/a5cf9146-e87b-4eed-994a-55bd323bc0a4?autoplay=false&loop=false&muted=false&preload=true&responsive=true"
            :video-height="550"
            :video-height-mobile="200"
        />

        <!-- ===================== GOLD INTRO (white) ===================== -->
        <section class="pb-[80px] lg:py-[150px] bg-white">
            <div class="container mx-auto px-4 lg:px-[120px] max-w-[1440px]">
                <div class="flex flex-col lg:flex-row gap-[30px] lg:gap-[50px] items-start">
                    <!-- ── LEFT COLUMN (575px, gap=50px between all blocks) ── -->
                    <div class="lg:w-[575px] flex-shrink-0 flex flex-col gap-[30px] lg:gap-[50px]">
                        <!-- Block 1: Title (Montserrat 44px w600, black, left) -->
                        <h2 class="text-[28px] lg:text-[44px] lg:leading-[57px] font-bold text-black">
                            <span class="text-gold-gradient">Gold</span> verschenkt man nicht nur, man denkt damit an die Zukunft
                        </h2>

                        <!-- Block 2: Body Text (SF Pro 18px w400, black, left) -->
                        <p class="font-pro text-[18px] lg:text-md leading-[23px] text-black">
                            Eine Goldmünze ist mehr als ein Geschenk.<br />
                            Viele Familien nutzen Edelmetalle, um langfristig Vermögen aufzubauen, Werte zu bewahren oder ihren Kindern einen finanziellen Grundstein mitzugeben.<br /><br />
                            Gold kann über Generationen hinweg Bestand haben – unabhängig von Inflation, Währungen oder Krisen.<br />
                            Genau deshalb haben wir unsere Osterkampagne diesem besonderen Edelmetall gewidmet.<br /><br />
                            Mit der Paint the Hopf Challenge verbinden wir Kreativität mit einem zeitlosen Wert: Gold.
                        </p>

                        <!-- Block 3: Promo Box (border #E2C750, p=20px, gap=30px, radius=20px, w=HUG~493px) -->
                        <div class="border-4 border-[#E2C750] rounded-[20px] p-[20px] lg:p-5 w-fit">
                            <!-- Promo title: Montserrat 24px mobile / 28px desktop, black, mb=30px -->
                            <h3 class="text-[24px] leading-[31px] lg:text-[28px] lg:leading-[36px] font-bold text-black mb-[30px]">Jetzt 20 % Rabatt auf Gold, Silber,<br class="lg:block hidden"> Edelmetalle und Minenaktien</h3>
                            <!-- Icons row: gap=30px, 4 × 90×90px (Figma gap=30px HORIZONTAL) -->
                            <div class="flex flex-row justify-between">
                                <div v-for="cat in productCategories" :key="cat.label" class="flex-shrink-0">
                                    <img :src="cat.icon" :alt="cat.label" class="w-[62px]  lg:w-[90px] lg:h-[90px] object-contain" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ── RIGHT COLUMN: Image (575×796px, radius=20px) ── -->
                    <div class="flex-1">
                        <img
                            :src="$asset('/hkcm/images/osterkampagne/goldbarren.png', 'public')"
                            alt="Gold Intro"
                            class="w-full lg:w-[575px] lg:h-[796px] object-cover rounded-[20px]"
                        />
                    </div>
                </div>
            </div>
        </section>

        <!-- ===================== PRIZES (dark gradient) ===================== -->
        <section id="teilnehmen" class="py-[80px] lg:py-[150px]" style="background: linear-gradient(to bottom, #072551, #020e20)">
            <div class="container mx-auto px-4 lg:px-[120px] max-w-[1440px]">
                <!-- Title -->
                <div class="text-center mb-[30px] lg:mb-[50px]">
                    <!-- "gewinnen" → Gradient -->
                    <h2 class="text-[28px] leading-[36px] lg:text-[44px] lg:leading-[57px] font-bold text-white text-center mb-4">Das können Sie <span class="text-gold-gradient">gewinnen</span></h2>
                    <p class="font-pro text-md leading-[23px] text-white text-center max-w-6xl mx-auto">
                        HKCM vergoldet Ihre Ostern mit unserer Kreativ-Kampagne „Paint the Hopf“. Statt einer klassischen Rabattaktion haben wir eine kreative Challenge gestartet.
                        Unsere Community gestaltet ein Bild von Philip Hopf: digital, gezeichnet oder mit KI.<br />
                        Die kreativsten Designs gewinnen echtes Gold.
                    </p>
                </div>
                <!-- Prize Cards -->
                <div class="flex flex-col lg:flex-row gap-5 mb-[30px] lg:mb-[50px]">
                    <!-- Hauptpreise -->
                    <div class="lg:w-1/2 bg-[#006CFF] rounded-[20px] p-[30px] flex flex-col lg:flex-row items-center gap-[20px] lg:gap-[30px]">
                        <img
                            :src="$asset('/hkcm/images/osterkampagne/goldHauptpreis.png', 'public')"
                            alt="Gold Hauptpreis"
                            class="lg:w-[246px] lg:h-[250px] h-[150px] object-contain rounded-[4px]"
                        />
                        <div>
                            <h3 class="text-[22px] leading-[29px] font-bold text-white mb-5">Hauptpreise</h3>
                            <p class="font-pro text-[18px] lg:text-md leading-[23px] text-white">
                                <strong>10 × 1/10 Unze Gold Lunar III Hase 2023</strong><br /><br />
                                Diese hochwertigen Sammlermünzen aus Gold sind weltweit begehrt und verbinden Sammlerwert mit Edelmetall-Investment.
                            </p>
                        </div>
                    </div>
                    <!-- Zusatzpreise -->
                    <div class="lg:w-1/2 bg-[#E5F0FF] rounded-[20px] p-[30px] flex flex-col lg:flex-row items-center gap-[20px] lg:gap-[30px]">
                        <img
                            :src="$asset('/hkcm/images/osterkampagne/osterZusatzpreis.png', 'public')"
                            alt="Zusatzpreis"
                            class="lg:w-[246px] lg:h-[150px] h-[150px] object-contain rounded-[20px]"
                        />
                        <div>
                            <h3 class="text-[22px] leading-[29px] font-bold text-black mb-5">Zusatzpreise</h3>
                            <p class="font-pro text-[18px] lg:text-md leading-[23px] text-black">
                                <strong>10 × 1g Goldbarren – Osteredition</strong><br /><br />
                                Diese Goldbarren eignen sich perfekt als:<br />
                                <ul style="list-style-type: disc; padding-left: 30px; ">
                                    <li class="text-sm"><span class="text-md font-pro text-black">Geschenk für Kinder</span></li>
                                    <li class="text-sm"><span class="text-md font-pro text-black">erste Edelmetall-Investition</span></li>
                                    <li class="text-sm"><span class="text-md font-pro text-black">symbolischer Vermögensbaustein</span></li>
                                </ul>
                            </p>
                        </div>
                    </div>
                </div>
                <!-- CTA -->
                <div class="text-center">
                    <router-link
                        to="#so-funktioniert-es"
                        class="justify-center lg:justify-normal flex lg:inline-flex items-center gap-2 px-5 py-5 rounded-full font-semibold text-[20px] text-white transition hover:opacity-90"
                        style="background: linear-gradient(to bottom right, #93802f, #e2c750)"
                    >
                        Jetzt teilnehmen
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                    </router-link>
                </div>
            </div>
        </section>

        <!-- ===================== OSTERAKTION "PAINT THE HOPF" (white) ===================== -->
        <section  class="bg-white">
            <div class="">
                <div class="container mx-auto px-4 lg:px-[120px] max-w-[1440px] pt-[80px] lg:pt-[150px]">
                    <!-- "„Paint the Hopf"" → Gradient -->
                    <h2 class="text-[28px] leading-[36px] lg:text-[44px] lg:leading-[57px] font-bold text-black mb-4">Unsere Osteraktion <span class="text-gold-gradient">„Paint the Hopf"</span></h2>
                    <p class="font-pro text-md leading-[23px] text-black mb-10">
                        Lassen Sie Ihrer Fantasie freien Lauf. Ob mit Buntstiften, Kreide, Wachsmalern oder Wasserfarben –<br />
                        oder mit Photoshop und KI: Alles ist erlaubt!
                    </p>
                </div>
                <!-- Gallery Placeholder Row -->
                <div class="flex flex-row overflow-x-hidden 2xl:justify-center">
                    <img :src="$asset('hkcm/images/osterkampagne/hopf-gallery-r1-3.png', 'public')" alt="Ostern" class="h-[250px] lg:h-[362px]" />
                    <img :src="$asset('/hkcm/images/osterkampagne/ph-ostern-7.png', 'public')" alt="Ostern" class="h-[250px] lg:h-[362px]" />
                    <img :src="$asset('/hkcm/images/osterkampagne/ph-ostern-11.png', 'public')" alt="Ostern" class="h-[250px] lg:h-[362px]" />
                    <img :src="$asset('/hkcm/images/osterkampagne/ph-ostern-6.png', 'public')" alt="Ostern" class="h-[250px] lg:h-[362px]" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r2-6.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-[362px]" />
                    <img :src="$asset('/hkcm/images/osterkampagne/ph-ostern-12.png', 'public')" alt="Ostern" class="h-[250px] lg:h-[362px]" />

                </div>
            </div>
        </section>

        <!-- ===================== HOW IT WORKS ===================== -->
        <!-- title-highlight-gradient activates CSS gradient on „Paint the Hopf" -->

        <NumberedListSection
            id="so-funktioniert-es"
            title="So funktioniert<br class='lg:hidden block'> „Paint the Hopf“"
            title-highlight="„Paint the Hopf“"
            :title-highlight-gradient="true"
            :items="targetAudienceItems"
            :image="'/hkcm/images/osterkampagne/ph-ostern-1.png'"
            image-alt="HKCM Analyst"
            :show-cta="true"
            cta-text="Vorlage herunterladen"
            cta-position="left"
            :cta-link="$asset('/hkcm/images/osterkampagne/ph-ostern-1.png')"
            :cta-download-link="true"
            :cta-prevent-default="true"
            button-gradient-from="#e2c750"
            button-gradient-to="#93802f"
            @cta-click="showOsterModal = true"
        />

        <!-- Oster Privacy Modal -->
        <OsterPrivacyModal v-model:show="showOsterModal" :download-url="$asset('/hkcm/images/osterkampagne/hkcm_design_vorlage_ostern2026.png')" />

        <!-- ===================== HOPF GALLERY (white) ===================== -->
        <section class="bg-white overflow-hidden">
            <!-- Title & Description: padded content area -->
            <div class="mx-auto px-4 lg:px-[120px] max-w-[1440px] pt-[80px] lg:pt-[150px]">
                <h2 class="text-[28px] leading-[36px] lg:text-[44px] lg:leading-[57px] font-bold text-black text-left">Die <span class="text-gold-gradient">Hopf</span> Gallery</h2>
                <p class="font-pro text-md leading-[23px] text-black text-left mt-[30px] lg:mt-[50px]">Beispiele aus unserer Community.</p>
            </div>

            <!-- Gallery: full-bleed, no horizontal padding, 50px below text -->
            <div class="mt-[30px] lg:mt-[50px] flex flex-col">
                <div class="flex flex-row lg:h-[362px] 2xl:justify-center">
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r1-1.png', 'public')" alt="Hopf Gallery" class="2xl:hidden block h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r1-6.png', 'public')" alt="Hopf Gallery" class="hidden 2xl:block h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r1-2.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r1-3.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r1-4.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r1-5.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r1-6.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r1-7.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r1-8.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                </div>

                <div class="flex flex-row lg:h-[362px] 2xl:justify-center">
                    <img
                        :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r2-1.png', 'public')"
                        alt="Hopf Gallery"
                        class="-ml-[747px] 2xl:ml-0 h-[250px] lg:h-full w-auto object-cover flex-shrink-0"
                    />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r2-8.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r2-3.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r2-4.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r2-5.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r2-6.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r2-7.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r2-8.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                    <img :src="$asset('/hkcm/images/osterkampagne/hopf-gallery-r2-9.png', 'public')" alt="Hopf Gallery" class="h-[250px] lg:h-full w-auto object-cover flex-shrink-0" />
                </div>
            </div>
            <!-- NO bottom padding: gallery goes to section bottom edge (Figma has no paddingBottom) -->
        </section>

        <!-- ===================== PRODUCTS / 20 % DISCOUNT (dark gradient) ===================== -->
        <section class="py-[80px] lg:py-[150px] relative overflow-hidden" style="background: linear-gradient(to bottom, #072551, #020e20)">
            <div class="container mx-auto px-4 lg:px-[120px] max-w-[1440px]">
                <!-- "20 % Oster-Rabatt" → Gradient -->
                <h2 class="text-[28px] leading-[36px] lg:text-[44px] lg:leading-[57px] font-bold text-white text-center mb-4"><span class="text-gold-gradient">20 % Oster-Rabatt</span> auf ausgewählte Analysen</h2>
                <p class="font-pro text-[18px] leading-[23px] text-white text-center max-w-4xl mx-auto mb-[30px] lg:mb-[50px]">
                    Neben der Challenge haben wir ein besonderes Osterangebot für unsere Community vorbereitet.<br />
                    Für kurze Zeit erhalten Sie <strong>20 % Rabatt</strong>, zusätzlich zu den Langzeitrabatten, auf unsere<br class="lg:block hidden" /> Gold-, Silber-, Edelmetalle- und
                    Minen-Analysepakete.<br />
                    Diese Analysen helfen Anlegern, Entwicklungen in den wichtigsten Edelmetallmärkten besser zu verstehen.
                </p>

                <!-- Mobile badge: "20 %" WHITE, "Rabatt" BLACK, "bis 12.04.2026" BLACK -->
                <div class="w-full h-fit bg-[#006CFF] lg:hidden rounded-full flex items-center justify-center shadow-xl mb-[30px] mx-auto">
                    <div class="text-center flex flex-col gap-0">
                        <div class="flex items-center justify-center gap-2">
                            <p class="font-bold text-white text-[60px] leading-[57px]">20 % <span class="text-black text-[34px] leading-[12px]">Rabatt</span></p>
                        </div>
                        <p class="font-pro text-[21px] font-semibold text-black mt-1">bis 12.04.2026</p>
                    </div>
                </div>

                <!-- "Pakete" → Gradient -->
                <h3 class="text-[26px] leading-[34px] lg:text-[32px] lg:leading-[42px] font-semibold text-white mb-8 text-center">Unsere <span class="text-gold-gradient">Pakete</span></h3>

                <!-- Product Cards -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-[50px] relative">
                    <div v-for="product in discountProducts" :key="product.name" class="bg-white rounded-[20px] px-5 pt-10 pb-10 flex flex-col items-center gap-5">
                        <div class="flex items-center justify-center">
                            <img :src="product.icon" alt="Product Image" class="w-[70px] h-[70px] object-contain" />
                        </div>
                        <div class="text-center">
                            <h4 class="text-[26px] leading-[34px] lg:text-[32px] lg:leading-[42px] font-semibold text-black">{{ product.name }}</h4>
                            <p class="font-pro text-md leading-[23px] text-black">{{ product.description }}</p>
                        </div>
                        <router-link
                            :to="product.link"
                            class="inline-flex items-center text-center gap-2 px-5 py-5 rounded-full font-semibold text-[20px] text-white transition hover:opacity-90 mx-auto"
                            style="background: linear-gradient(to bottom right, #93802f, #e2c750)"
                        >
                            {{ product.cta }}
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </router-link>
                    </div>
                    <!-- Discount Badge (desktop): "20 %" WHITE, "Rabatt" BLACK -->
                    <div class="hidden lg:flex absolute top-[-180px] right-[-100px] w-[250px] h-[250px] bg-[#006CFF] rounded-full items-center justify-center shadow-xl z-10">
                        <div class="text-center leading-tight">
                            <p class="font-bold text-white text-[70px] leading-[57px]">20 %</p>
                            <p class="font-semibold text-black text-[44px] leading-[57px]">Rabatt</p>
                            <p class="font-pro text-[21px] font-semibold text-black ">bis 12.04.2026</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ===================== WHY GOLD FOR FAMILY (white) ===================== -->
        <section
            class="py-[80px] lg:py-[150px] bg-white"
            :style="{
                backgroundImage: 'url(' + $asset('/hkcm/images/bg_candels.png', 'public') + ')',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: 'cover'
            }"
        >
            <div class="mx-auto px-[20px] lg:px-[0] max-w-[1200px]">
                <!-- "Gold" → Gradient -->
                <h2 class="text-[28px] leading-[36px] lg:text-[44px] lg:leading-[57px] font-bold text-black mb-[30px] lg:mb-[50px]">Warum viele Menschen <span class="text-gold-gradient">Gold</span> für ihre Familie kaufen</h2>
                <p class="font-pro text-[18px] leading-[23px] text-black mb-[30px] lg:mb-[50px]">
                    Gold begleitet Familien seit Generationen.<br />
                    Viele Menschen schenken ihren Partnern, Kindern oder Geschwistern zu besonderen Anlässen eine erste Goldmünze oder einen kleinen Goldbarren: als Symbol für
                    Zukunft, Sicherheit und langfristigen Wert.<br />
                    Während Konsumgüter mit der Zeit verschleißen und Geld auf dem Konto an Kaufkraft verlieren kann, bleibt Gold über Jahrzehnte hinweg ein stabiler Sachwert.<br />
                    Genau deshalb entscheiden sich viele Menschen dafür, einen kleinen Teil ihres Vermögens in Edelmetallen aufzubauen – für sich selbst oder für die nächste
                    Generation.
                </p>
                <!-- 3 Feature Cards -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-[20px]">
                    <div v-for="card in whyGoldCards" :key="card.title" class="bg-[#E5F0FF] rounded-[20px] p-[30px] flex flex-col items-center gap-[20px]">
                        <img :src="$asset(card.icon, 'public')" :alt="card.title" class="w-[70px] h-[70px] object-contain" />
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <h3 class="text-[24px] leading-[31px] font-semibold text-black text-center" v-html="card.title"></h3>
                        <p class="font-pro text-[18px] leading-[23px] text-black text-center">{{ card.description }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- ===================== ADDITIONAL INFO (dark gradient) ===================== -->
        <section class="py-[80px] lg:py-[150px]" style="background: linear-gradient(to bottom, #072551, #020e20)">
            <div class="mx-auto px-4 lg:px-[120px] max-w-[1440px]">
                <div class="flex flex-col-reverse lg:flex-row gap-[30px] lg:gap-[50px] items-center">
                    <!-- ── LEFT COLUMN ── -->
                    <div class="lg:w-[575px] flex-shrink-0 flex flex-col gap-[30px] lg:gap-[50px]">
                        <!-- "goldenen" → Gradient -->
                        <h2 class="text-[28px] leading-[36px] lg:text-[44px] lg:leading-[57px] font-bold text-white ">Machen Sie Ostern zu einem <span class="text-gold-gradient">goldenen</span> Moment</h2>

                        <!-- Body Text (SF Pro 18px, white, left-aligned, exact Figma copy) -->
                        <div>
                        <p class="font-pro text-md leading-[23px] text-white">
                            Nehmen Sie jetzt an der Paint-the-Hopf-Challenge teil und gewinnen Sie echtes Gold.<br />
                            Zusätzlich sichern Sie sich 20 % Oster-Rabatt auf diese Analyse-Pakete:<br />
                        </p>
                        <ul style="list-style-type: disc; padding-left: 30px; margin-top: 10px; margin-bottom: 10px" class="font-pro text-md leading-[23px] text-white">
                            <li>Gold</li>
                            <li>Silber</li>
                            <li>Edelmetallen</li>
                            <li>Minenaktien</li>
                        </ul>

                        <p class="font-pro text-white">
                            Entdecken Sie, warum viele Anleger Edelmetalle als wichtigen Bestandteil ihres Portfolios betrachten und vielleicht beginnt Ihre eigene Goldgeschichte genau heute.
                        </p>
                        </div>

                        <!-- CTA Buttons (gap=20px, each auto-width / HUG, radius=50px, Montserrat 20px) -->
                        <div class="flex flex-col gap-[20px]">
                            <router-link
                                to="/produkte/gold"
                                class="text-center flex justify-center lg:justify-normal lg:inline-flex w-full lg:w-fit items-center gap-[10px] px-5 h-[64px] rounded-full font-bold text-[20px] leading-[24px] text-white transition hover:opacity-90"
                                style="background: linear-gradient(to right, #93802f, #e2c750)"
                            >
                                20 % Rabatt auf Gold
                                <svg class="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </router-link>
                            <router-link
                                to="/produkte/silber"
                                class=" text-center flex justify-center lg:justify-normal lg:inline-flex w-full lg:w-fit items-center gap-[10px] px-5 h-[64px] rounded-full font-bold text-[20px] leading-[24px] text-white transition hover:opacity-90"
                                style="background: linear-gradient(to right, #93802f, #e2c750)"
                            >
                                20 % Rabatt auf Silber
                                <svg class="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </router-link>
                            <router-link
                                to="/produkte/edelmetalle"
                                class="flex justify-center lg:justify-normal lg:inline-flex text-center w-full lg:w-fit items-center gap-[10px] px-4 h-[64px]  rounded-full font-bold text-[20px] leading-[24px] text-white transition hover:opacity-90"
                                style="background: linear-gradient(to right, #93802f, #e2c750)"
                            >
                                20 % Rabatt auf Edelmetalle
                                <svg class="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </router-link>
                            <router-link
                                to="/produkte/minen"
                                class="flex justify-center lg:justify-normal lg:inline-flex text-center w-full lg:w-fit items-center gap-[10px] px-5 h-[64px] rounded-full font-bold text-[20px] leading-[24px] text-white transition hover:opacity-90"
                                style="background: linear-gradient(to right, #93802f, #e2c750)"
                            >
                                20 % Rabatt auf Minen-Aktien
                                <svg class="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </router-link>
                        </div>
                    </div>

                    <!-- ── RIGHT COLUMN: Image + Badge ── -->
                    <div class="relative flex-1 flex justify-center lg:justify-end">
                        <!-- Image: 575×796px, radius=20px -->
                        <div class="relative">
                            <img
                                :src="$asset('/hkcm/images/osterkampagne/ph-ostern-2.png', 'public')"
                                alt="HKCM Osterkampagne"
                                class="w-full lg:w-[575px] lg:h-[796px] object-cover rounded-[20px]"
                            />

                            <!-- Badge: "20 %" WHITE, "Rabatt" BLACK -->
                            <div class="absolute w-[250px] h-[250px] rounded-full bg-[#006CFF] flex items-center justify-center shadow-xl lg:top-[-105px] lg:left-[-50px] top-[-150px] left-1/2 transform -translate-x-1/2 lg:translate-x-0">
                                <p class="font-bold text-[44px] leading-[57px] text-center"><span class="text-white text-[80px]">20 %</span><br /><span class="text-black text-[49px]">Rabatt</span></p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <!-- ===================== FOOTER ===================== -->
<!--        <LandingFooter-->
<!--            background-color="#FFFFFF"-->
<!--            logo="/hkcm/images/logo-on-light-background.svg"-->
<!--            logo-link="/produkte"-->
<!--            link-color="text-gray-600"-->
<!--            :links="footerLinks"-->
<!--            :show-cta="false"-->
<!--        />-->

        <footer class="py-[15px] border-t border-gray-200 bg-white">
            <div class="px-5 lg:px-[120px]">
                <div class="flex flex-col lg:flex-row justify-between items-center gap-6 lg:gap-0">
                    <router-link :to="'/'" class="flex items-center gap-2">
                        <img class="w-[169px] h-[50px] object-contain" :src="$asset('/hkcm/images/logo-on-light-background.svg', 'public')" alt="HKCM Logo" title="HKCM Logo" />
                    </router-link>
                    <div class="flex flex-row">
                        <router-link to="/impressum" class="text-[#031837] font-semibold mx-2">
                            Impressum
                        </router-link>
                        <router-link to="/datenschutz" class="text-[#031837] font-semibold  mx-2">
                            Datenschutz
                        </router-link>
                        <router-link to="/agb" class="text-[#031837] font-semibold mx-2">
                            AGB
                        </router-link>

                        <div class="lg:flex flex-col lg:flex-row text-center gap-5 lg:gap-0 hidden">
                            <router-link to="/verlosungdatenschutz" class="text-[#031837] font-semibold mx-2">
                                Nutzungsrechte für UGC
                            </router-link>
                            <router-link to="/verlosung" class="text-[#031837] font-semibold  mx-2">
                                Teilnahmebedingungen
                            </router-link>

                        </div>
                    </div>
                    <div class="flex flex-col lg:flex-row text-center gap-5 lg:gap-0 lg:hidden">
                        <router-link to="/verlosungdatenschutz" class="text-[#031837] font-semibold mx-2">
                            Nutzungsrechte für UGC
                        </router-link>
                        <router-link to="/verlosung" class="text-[#031837] font-semibold  mx-2">
                            Teilnahmebedingungen
                        </router-link>

                    </div>

                </div>
            </div>
        </footer>

    </div>
</template>

<script setup>
import { ref } from 'vue';
import i18next from 'i18next';
import LandingNav from '../components/landing/LandingNav.vue';
import HeroSection from '../components/landing/HeroSection.vue';
import VideoSection from '../components/landing/VideoSection.vue';
import LandingFooter from '../components/landing/LandingFooter.vue';
import NumberedListSection from '../components/landing/NumberedListSection.vue';
import OsterPrivacyModal from '../components/landing/OsterPrivacyModal.vue';

// Modal state
const showOsterModal = ref(false);

const targetAudienceItems = [
    {
        icon: '/hkcm/images/osterkampagne/step-1-download.svg',
        title: 'Schritt 1',
        description: 'Vorlage herunterladen <br> Laden Sie unsere offizielle Hopf-Vorlage herunter.'
    },
    {
        icon: '/hkcm/images/osterkampagne/step-2-paintbrush.svg',
        title: 'Schritt 2',
        description:
            'Kreativ werden<br>Bemalen oder gestalten Sie die Vorlage.<br>Erlaubt ist alles:<br>&nbsp;&nbsp;&bull; Zeichnungen<br>&nbsp;&nbsp;&bull; digitale Designs<br>&nbsp;&nbsp;&bull; KI-Kunst<br>&nbsp;&nbsp;&bull; Memes'
    },
    {
        icon: '/hkcm/images/osterkampagne/step-3-share.svg',
        title: 'Schritt 3',
        description:
            'Posten Sie Ihr Design in Ihrem Instagram-Feed.<br>Erwähnen Sie:<br>&nbsp;&nbsp;&bull; @hkc_management und<br>&nbsp;&nbsp;&bull; drei Freunde (mit @Handles) in der Caption<br>&nbsp;&nbsp;&bull; unseren Kampagnen-Hashtag #paintthehopf'
    }
];

// App Store / Google Play
const appStoreData = {
    badge: i18next.language === 'de' ? '/hkcm/images/apple-store-badge.png' : '/hkcm/images/apple-store-badge-en.png',
    link: 'https://apps.apple.com/de/app/hkcm/id6499010943',
    rating: '4.4',
    stars: '/hkcm/images/stars4_5_yellow.png',
    reviews: '1.8k'
};

const googlePlayData = {
    badge: i18next.language === 'de' ? '/hkcm/images/google-play-badge.png' : '/hkcm/images/google-play-badge-en.png',
    link: 'https://play.google.com/store/apps/details?id=de.hkcm.app&hl=de&pli=1',
    rating: '4.5',
    stars: '/hkcm/images/stars4_5_green.png',
    reviews: '7.3k'
};

// Product Categories (Promo Box)
const productCategories = [
    { label: 'Gold', icon: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/gold-gradient.png' },
    { label: 'Silber', icon: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/silber-gradient.png' },
    { label: 'Edelmetalle', icon: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/edelmetalle-gradient.png' },
    { label: 'Minenaktien', icon: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/minen-gradient.png' }
];

// Discount Products (Section 8)
const discountProducts = [
    {
        name: 'Gold',
        description: 'Analysen des wichtigsten Edelmetalls der Welt.',
        icon: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/gold-gradient.png',
        cta: '20 % Rabatt auf Gold',
        link: '/produkte/gold'
    },
    {
        name: 'Silber',
        description: 'Der unterschätzte Rohstoff zwischen Industrie und Investment.',
        icon: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/silber-gradient.png',
        cta: '20 % Rabatt auf Silber',
        link: '/produkte/silber'
    },
    {
        name: 'Edelmetalle',
        description: 'Analysen der wichtigsten Edelmetalle der Welt.',
        icon: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/edelmetalle-gradient.png',
        cta: '20 % Rabatt auf Edelmetalle',
        link: '/produkte/edelmetalle'
    },
    {
        name: 'Minenaktien',
        description: 'Analysen der wichtigsten Minenwerte.',
        icon: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/minen-gradient.png',
        cta: '20 % Rabatt auf Minen-Aktien',
        link: '/produkte/minen'
    }
];

// Why Gold Cards (Section 9)
const whyGoldCards = [
    {
        title: 'Langfristiger<br> Vermögensaufbau',
            icon: '/hkcm/images/osterkampagne/why-gold-icon-trend-up.svg',
        description:
            'Gold wird häufig als langfristiger Baustein für Vermögen genutzt. Über viele Jahre hinweg kann es helfen, Werte zu bewahren und finanzielle Rücklagen aufzubauen.'
    },
    {
        title: 'Sicherheit &<br> Stabilität',
        icon: '/hkcm/images/osterkampagne/why-gold-icon-shield-check.svg',
        description:
            'Gold wird von vielen Menschen als stabiler Sachwert wahrgenommen. Gerade in unsicheren Zeiten setzen viele auf Edelmetalle, um ihr Vermögen breiter aufzustellen und Risiken zu streuen.'
    },
    {
        title: 'Ein Geschenk mit<br> Bedeutung',
        icon: '/hkcm/images/osterkampagne/why-gold-icon-gift.svg',
        description: 'Eine Goldmünze ist mehr als nur ein materieller Wert. Sie ist ein Geschenk mit Symbolkraft: für Verantwortung, Zukunft und finanzielle Bildung.'
    }
];

// Hero badges
const heroBadges = [
    { src: '/hkcm/images/partners/BlackBull24_Krypto_square.png', alt: 'Black Bull Award Winner' },
    { src: '/hkcm/images/partners/BlackBull25_InvestmentVision.png', alt: 'Investment Visionary Award' },
    { src: '/hkcm/images/partners/DISQTrust_Siegel_Von Kunden_Empfohlen_HKCM.png', alt: 'DISQ Trust', class: 'w-full object-contain max-h-[180px] lg:w-auto' }
];

// Footer Links
const footerLinks = [
    { text: 'Impressum', to: '/impressum' },
    { text: 'AGB', to: '/agb' },
    { text: 'Datenschutz', to: '/datenschutz' },
    { text: 'Nutzungsrechte für UGC', to: '/nutzungsrechte-ugc' },
    { text: 'Teilnahmebedingungen', to: '/teilnahmebedingungen-osterkampagne' }
];
</script>

<style scoped>
.font-body {
    overflow-x: hidden;
}

/* Gold gradient text — matches Figma GRADIENT_LINEAR (to top: #93802F → #E2C750) */
.text-gold-gradient {
    background: linear-gradient(to top, #93802f, #e2c750);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}
</style>
